import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CourseCard from './CourseCard';
import { PlusIcon } from '@heroicons/react/solid';
import {
  getCoursesOverview,
  getAllCourses,
  updateCourse as updateCourseAction,
} from '../../../redux/actions/elearningActions';
import Loader from '../../../components/core/Loader';
import { RootState } from '../../../redux/store';
import Course from './types/course.d';
import EditCourseModal from './EditCourseModal';
import { useTranslation } from 'react-i18next';

function CourseMainContent() {
  const courses = useSelector((state: RootState) => state.elearning.courses);
  const user = useSelector((state: RootState) => state.user.singleUser);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const { t } = useTranslation();

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getCoursesOverview());
    dispatch(getAllCourses());
  }, [dispatch]);

  const updateCourse = async (fields: any) => {
    // TODO: Remove 'any'
    await dispatch(updateCourseAction(fields));
    await dispatch(getAllCourses());
    return true;
  };

  const sortedCourses = useMemo(() => {
    return courses.sort((e1: Course, e2: Course) => {
      if (e1.position > e2.position) {
        return 1;
      }
      if (e1.position < e2.position) {
        return -1;
      }
      return 0;
    });
  }, [courses]);

  if (!user) {
    return <Loader />;
  }

  return (
    <main className="flex flex-col space-y-6 m-3 xl:m-5">
      {/* Headers */}
      <div className="flex justify-between">
        {/* Title etc */}
        <div className="flex flex-col">
          <h1 className="font-bold text-grey-dark text-xl">E-learning</h1>
          <h2 className="font-normal text-grey-medium">{`Cursussen voor ${user.firstName}`}</h2>
        </div>

        {/* New Course Button for admins. Was previously
            `text-sky hover:text-sky-dark flex space-x-1` — only colored
            text, no bg/padding/rounded/shadow, so it rendered as a link
            instead of a button. Matched to the solid-sky button shape
            used elsewhere in this file (CourseCard's Meer-zien). */}
        {(user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') && (
          <button
            className="bg-sky hover:bg-sky-dark text-white rounded-md shadow-md hover:shadow-lg px-4 py-2 flex items-center space-x-2"
            onClick={() => setModalOpen(true)}
          >
            <PlusIcon className="w-4" />
            <p>{t('courses.new_course')}</p>
          </button>
        )}
      </div>

      {/* List of available courses */}
      <section className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {sortedCourses.map((course: Course) => {
          return (
            <CourseCard
              key={course._id}
              id={course._id}
              image={course.imageUrl}
              lessons={course.lessons.length}
              members={course.members.length}
              title={course.title}
              joined={false}
              description={course.description}
              position={course.position}
              updateCourse={updateCourse}
              isAdmin={user.role === 'ADMIN' || user.role === 'SUPER_ADMIN'}
              onClickEdit={() => {
                setSelectedCourse(course);
                setModalOpen(true);
              }}
            />
          );
        })}
      </section>

      {/* Modal */}
      {modalOpen && (
        <EditCourseModal
          open={modalOpen}
          setOpen={(val) => {
            setModalOpen(val);
            setSelectedCourse(null);
          }}
          submit={updateCourse}
          course={selectedCourse}
        />
      )}
    </main>
  );
}

export default CourseMainContent;
